import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  getLocationOccupancy,
  type LocationOccupancyData,
  type LocationOccupancySession,
} from "../../services/api";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { ClubNavIcon } from "../../layouts/clubNav";

export default function ClubCalendar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClubId } = useAuth();

  const [calendarData, setCalendarData] = useState<LocationOccupancyData | null>(null);
  const [calendarWeekStart, setCalendarWeekStart] = useState<Date>(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const [loading, setLoading] = useState(true);
  // Mobile-only: which day of the current week is selected in the
  // day-picker. Defaults to today if today is in the week, else Monday.
  const [mobileSelectedDayIdx, setMobileSelectedDayIdx] = useState<number>(() => {
    const todayIdx = new Date().getDay();
    return todayIdx === 0 ? 6 : todayIdx - 1;
  });

  useEffect(() => {
    if (!activeClubId) return;

    const loadCalendarData = async () => {
      setLoading(true);
      try {
        const weekEnd = new Date(calendarWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        const data = await getLocationOccupancy(
          activeClubId,
          calendarWeekStart.toISOString(),
          weekEnd.toISOString()
        );
        setCalendarData(data);
      } catch (error) {
        console.error("Failed to load calendar data:", error);
      }
      setLoading(false);
    };

    loadCalendarData();
  }, [activeClubId, calendarWeekStart]);

  // Calendar navigation
  const goToPrevWeek = () => {
    setCalendarWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const goToNextWeek = () => {
    setCalendarWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  const goToCurrentWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    setCalendarWeekStart(monday);
  };

  // Get days of the week for calendar header
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(calendarWeekStart);
      day.setDate(calendarWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }, [calendarWeekStart]);

  // Get sessions for a specific location and day
  const getSessionsForLocationAndDay = (locationId: string, day: Date): LocationOccupancySession[] => {
    if (!calendarData) return [];
    const sessions = calendarData.occupancyByLocation[locationId] || [];
    return sessions.filter(session => {
      const sessionDate = new Date(session.start_at);
      return sessionDate.toDateString() === day.toDateString();
    }).sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  };

  if (loading && !calendarData) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
      <AdminPageHeader
        title={t("admin.calendar", "Calendar")}
        subtitle={t("admin.locationOccupancy", "Calendar ocupare locații")}
        icon={<ClubNavIcon id="calendar" className="w-6 h-6" />}
        actions={
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <button
              onClick={goToPrevWeek}
              className="p-2 transition-colors rounded-lg text-slate-600 hover:bg-slate-100"
              title={t("common.previousWeek", "Săptămâna anterioară")}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToCurrentWeek}
              className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              {t("common.today", "Azi")}
            </button>
            <button
              onClick={goToNextWeek}
              className="p-2 transition-colors rounded-lg text-slate-600 hover:bg-slate-100"
              title={t("common.nextWeek", "Săptămâna următoare")}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-slate-700 whitespace-nowrap">
              {calendarWeekStart.toLocaleDateString("ro-RO", { day: "numeric", month: "short" })} - {
                new Date(calendarWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" })
              }
            </span>
          </div>
        }
      />
      <div className="p-3 sm:p-6 bg-white border border-slate-200 rounded-2xl shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
        ) : !calendarData || calendarData.locations.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p>{t("admin.noLocationsForCalendar", "Nicio locație configurată. Adaugă locații mai întâi pentru a vedea calendarul de ocupare.")}</p>
            <button
              onClick={() => navigate("/dashboard/admin-club/locations")}
              className="px-4 py-2 mt-3 text-sm text-white transition-colors bg-indigo-500 rounded-lg hover:bg-indigo-600"
            >
              {t("admin.goToLocations", "Mergi la locații")}
            </button>
          </div>
        ) : (
          <>
          {/* Mobile view: day picker + per-location session list for the
              selected day. The desktop table below (hidden sm:block) is too
              wide for phones, so on mobile we collapse to a single-day view. */}
          <div className="sm:hidden">
            <div className="grid grid-cols-7 gap-1 mb-4">
              {weekDays.map((day, idx) => {
                const isToday = day.toDateString() === new Date().toDateString();
                const isSelected = idx === mobileSelectedDayIdx;
                const dayNamesRo = ["Dum", "Lun", "Mar", "Mie", "Joi", "Vin", "Sâm"];
                return (
                  <button
                    key={idx}
                    onClick={() => setMobileSelectedDayIdx(idx)}
                    className={`p-1.5 rounded-lg text-center transition-colors border ${
                      isSelected
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : isToday
                          ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-[10px] font-semibold leading-tight">{dayNamesRo[day.getDay()]}</div>
                    <div className="text-sm font-bold leading-tight">{day.getDate()}</div>
                  </button>
                );
              })}
            </div>
            <div className="space-y-3">
              {calendarData.locations.map((location) => {
                const day = weekDays[mobileSelectedDayIdx];
                const daySessions = getSessionsForLocationAndDay(location.id, day);
                return (
                  <div key={location.id} className="p-3 rounded-xl border border-slate-200 bg-slate-50">
                    <div className="font-semibold text-sm text-slate-800 break-words">{location.name}</div>
                    {location.address && (
                      <div className="text-xs text-slate-400 mt-0.5 break-words">{location.address}</div>
                    )}
                    <div className="mt-2 space-y-1.5">
                      {daySessions.length === 0 ? (
                        <div className="text-xs text-slate-400 italic">{t("admin.noSessionsThisDay", "Fără sesiuni")}</div>
                      ) : (
                        daySessions.map((session) => {
                          const startTime = new Date(session.start_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
                          const endTime = new Date(session.end_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
                          const isEducational = session.type === "educational";
                          return (
                            <div
                              key={session.id}
                              className={`p-2 rounded text-xs ${
                                isEducational
                                  ? "bg-purple-100 text-purple-800 border border-purple-200"
                                  : "bg-emerald-100 text-emerald-800 border border-emerald-200"
                              }`}
                            >
                              <div className="font-semibold">{startTime} - {endTime}</div>
                              {session.team?.name && <div className="opacity-80 break-words">{session.team.name}</div>}
                              {(session.coach?.full_name || session.coach?.email) && (
                                <div className="opacity-70 break-words">{session.coach?.full_name || session.coach?.email}</div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full border-collapse min-w-[800px]">
              <thead>
                <tr>
                  <th className="w-40 p-3 text-sm font-semibold text-left border border-slate-200 bg-slate-50 text-slate-700">
                    {t("admin.location", "Locație")}
                  </th>
                  {weekDays.map((day, idx) => {
                    const isToday = day.toDateString() === new Date().toDateString();
                    const dayNamesRo = ["Dum", "Lun", "Mar", "Mie", "Joi", "Vin", "Sâm"];
                    return (
                      <th
                        key={idx}
                        className={`border border-slate-200 p-2 text-center text-sm font-medium ${
                          isToday ? "bg-indigo-50 text-indigo-700" : "bg-slate-50 text-slate-600"
                        }`}
                      >
                        <div className="font-semibold">{dayNamesRo[day.getDay()]}</div>
                        <div className={`text-xs ${isToday ? "text-indigo-600" : "text-slate-400"}`}>
                          {day.getDate()}/{day.getMonth() + 1}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {calendarData.locations.map((location) => (
                  <tr key={location.id}>
                    <td className="p-3 bg-white border border-slate-200">
                      <div className="text-sm font-medium text-slate-800">{location.name}</div>
                      {location.address && (
                        <div className="text-xs text-slate-400 mt-0.5 truncate max-w-[150px]" title={location.address}>
                          {location.address}
                        </div>
                      )}
                    </td>
                    {weekDays.map((day, idx) => {
                      const isToday = day.toDateString() === new Date().toDateString();
                      const daySessions = getSessionsForLocationAndDay(location.id, day);
                      return (
                        <td
                          key={idx}
                          className={`border border-slate-200 p-1.5 align-top ${
                            isToday ? "bg-indigo-50/30" : "bg-white"
                          }`}
                          style={{ minWidth: "100px", height: "80px" }}
                        >
                          {daySessions.length === 0 ? (
                            <div className="py-2 text-xs text-center text-slate-300">-</div>
                          ) : (
                            <div className="space-y-1">
                              {daySessions.map((session) => {
                                const startTime = new Date(session.start_at).toLocaleTimeString("ro-RO", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                });
                                const endTime = new Date(session.end_at).toLocaleTimeString("ro-RO", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                });
                                const isEducational = session.type === "educational";
                                return (
                                  <div
                                    key={session.id}
                                    className={`p-1.5 rounded text-xs ${
                                      isEducational
                                        ? "bg-purple-100 text-purple-800 border border-purple-200"
                                        : "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                    }`}
                                    title={`${session.title}\n${session.team?.name || "Fără echipă"}\n${session.coach?.full_name || session.coach?.email || "Fără antrenor"}`}
                                  >
                                    <div className="font-semibold truncate">{startTime} - {endTime}</div>
                                    <div className="truncate opacity-80">{session.team?.name || "-"}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Legend */}
        {calendarData && calendarData.locations.length > 0 && (
          <div className="flex items-center gap-4 pt-4 mt-4 text-xs border-t border-slate-200">
            <span className="text-slate-500">{t("admin.legend", "Legendă")}:</span>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 border rounded bg-emerald-100 border-emerald-200"></span>
              <span className="text-slate-600">{t("admin.sportSession", "Sport")}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-purple-100 border border-purple-200 rounded"></span>
              <span className="text-slate-600">{t("admin.educationalSession", "Educațional")}</span>
            </div>
          </div>
        )}
      </div>
      </div>
    </ClubAdminLayout>
  );
}
